"use client";

import { useTranslation } from "react-i18next";
import { MapPin, Clock } from "lucide-react";

const GOOGLE_MAPS_URL =
  "https://www.google.com/maps/search/?api=1&query=Eduardo+Sfeir+2827+El+Bolsón+Río+Negro+Argentina";

export type PitchVenueDetailsProps = {
  /** Localized string for the sports line (parquet vs synthetic) */
  sportsDescription: string;
};

export default function PitchVenueDetails({ sportsDescription }: PitchVenueDetailsProps) {
  const { t } = useTranslation();

  return (
    <div className="mb-10 rounded-2xl border border-slate-200/90 bg-white p-6 shadow-[0_2px_24px_-12px_rgba(15,23,42,0.12)] sm:p-8">
      <h2 className="sr-only">{t("canchaDetails.title")}</h2>
      <div>
        <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
          {t("canchaDetails.sports")}
        </h3>
        <p className="mt-2 text-slate-600">{sportsDescription}</p>
      </div>
      <hr className="my-6 border-slate-200" />

      <div>
        <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
          {t("canchaDetails.location")}
        </h3>
        <div className="mt-2 flex gap-3">
          <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-slate-400" />
          <div>
            <p className="leading-relaxed text-slate-600">
              {t("canchaPitchPage.addressLine1")}
              <br />
              {t("canchaPitchPage.addressLine2")}
            </p>
            <p className="mt-1 text-sm text-slate-500">{t("footer.locationNote")}</p>
            <a
              href={GOOGLE_MAPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-block font-medium text-primary underline underline-offset-2 hover:text-primary-strong"
            >
              {t("canchaDetails.viewOnMaps")}
            </a>
          </div>
        </div>
      </div>
      <hr className="my-6 border-slate-200" />

      <div>
        <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
          {t("canchaDetails.timings")}
        </h3>
        <div className="mt-2 flex gap-3">
          <Clock className="mt-0.5 h-5 w-5 shrink-0 text-slate-400" />
          <div>
            <p className="font-medium uppercase tracking-wide text-slate-600">{t("faq.a6")}</p>
            <a
              href="#pricing"
              className="mt-2 inline-block font-medium text-primary underline underline-offset-2 hover:text-primary-strong"
            >
              {t("canchaDetails.seeAllTimings")}
            </a>
          </div>
        </div>
      </div>
      <hr className="my-6 border-slate-200" />

      <div>
        <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
          {t("canchaDetails.facilities")}
        </h3>
        <ul className="mt-3 grid list-inside list-disc grid-cols-1 gap-x-8 gap-y-2 text-slate-600 sm:grid-cols-2">
          <li>{t("canchaDetails.parking")}</li>
          <li>{t("canchaDetails.restroom")}</li>
          <li>{t("canchaDetails.drinkingWater")}</li>
          <li>{t("canchaDetails.ledLighting")}</li>
          <li>{t("canchaDetails.lockersShowers")}</li>
        </ul>
      </div>
    </div>
  );
}
